// Server-backed overlay config. Replaces the v1 localStorage hooks.
// A tiny module-level cache stops multiple consumers racing the same fetch,
// and a custom event keeps them in sync when one consumer mutates.
// (The v1 logos half — useLogos/useLogosForOverlay/LogoUploader — was dead
// UI and has been removed; the legacy /api/logos endpoints remain server-side
// for the OBS asset contract.)

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.js';

const INV_EVENT = 'streamgg:invalidate';

function fireInvalidate(scope) {
  window.dispatchEvent(new CustomEvent(INV_EVENT, { detail: scope }));
}

// ─── overlay configs (stream + station) ────────────────────────────────────

let _configsCache = null;
let _configsPromise = null;

async function loadConfigs(force = false) {
  if (!force && _configsCache) return _configsCache;
  if (!_configsPromise) {
    _configsPromise = api.getOverlayConfig()
      .then((data) => {
        _configsCache = { stream: data?.stream ?? null, station: data?.station ?? null };
        return _configsCache;
      })
      .finally(() => { _configsPromise = null; });
  }
  return _configsPromise;
}

function useConfig(kind) {
  const [value, setValue] = useState(() => _configsCache?.[kind] ?? null);
  const [loading, setLoading] = useState(() => _configsCache == null);

  useEffect(() => {
    let alive = true;
    function apply() {
      if (!alive) return;
      setValue(_configsCache?.[kind] ?? null);
      setLoading(false);
    }
    function onInv(e) {
      if (!e.detail || e.detail === 'configs') apply();
    }
    window.addEventListener(INV_EVENT, onInv);
    if (_configsCache) apply();
    else loadConfigs().then(apply).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; window.removeEventListener(INV_EVENT, onInv); };
  }, [kind]);

  const setter = useCallback(async (next) => {
    if (next == null) {
      await api.deleteOverlayConfig(kind);
    } else {
      await api.putOverlayConfig(kind, next);
    }
    if (!_configsCache) _configsCache = { stream: null, station: null };
    _configsCache = { ..._configsCache, [kind]: next };
    fireInvalidate('configs');
  }, [kind]);

  return [value, setter, { loading }];
}

export function useStreamConfig() { return useConfig('stream'); }

// Drop all client-side caches without touching the server. Called on
// login + logout so a tab that switches users doesn't show (and worse,
// re-save) the previous user's overlay-config. The module-level caches
// are tab-scoped, not user-scoped, so without this they survive across
// sessions.
export function resetOverlayCaches() {
  _configsCache = null;
  _configsPromise = null;
  fireInvalidate();
}
